import "@/index.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HeroSection from "./components/HeroSection";
import ServicesSection from "./components/ServicesSection";
import FleetSection from "./components/FleetSection";
import ReviewsSection from "./components/ReviewsSection";
import PriceCalculator from "./components/PriceCalculator";
import BookingForm from "./components/BookingForm";
import AvailabilityForm from "./components/AvailabilityForm";
import ContactForm from "./components/ContactForm";
import AdminLogin from "./components/AdminLogin";
import AdminDashboard from "./components/AdminDashboard";
import WhatsAppButton from "./components/WhatsAppButton";
import InstallPWA from "./components/InstallPWA";
import ServiceCards from "./components/ServiceCards";
import Impressum from "./components/Impressum";
import PrivacyPolicy from "./components/PrivacyPolicy";
import CookieConsent from "./components/CookieConsent";

// Splash Screen
const SplashScreen = ({ onFinish }) => {
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setFadeOut(true), 2000);
    const finish = setTimeout(() => onFinish(), 2600);
    return () => { clearTimeout(timer); clearTimeout(finish); };
  }, [onFinish]);

  return (
    <div
      className={`fixed inset-0 z-[9999] bg-[#0d1117] flex flex-col items-center justify-center transition-opacity duration-600 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
      data-testid="splash-screen"
    >
      <img
        src="https://customer-assets.emergentagent.com/job_dumitru-transport/artifacts/eyga6rd8_att.aaSbiMv2BES3xiIlKCtGHUJBVxN4JzQsDKl0qxaVIZo.jpeg"
        alt="Exclusive Tour Transfer"
        className="w-64 sm:w-80 mb-8 object-contain"
      />
      <div className="w-48 h-[2px] bg-white/10 overflow-hidden rounded-full">
        <div className="h-full bg-[#D4AF37] animate-loading-bar" />
      </div>
    </div>
  );
};

// Home Page
const HomePage = () => {
  return (
    <>
      <HeroSection />
      <FleetSection />
      <ServiceCards />
      <ReviewsSection />
      <ServicesSection />
      <PriceCalculator />
    </>
  );
};

// Booking Page with Availability Check
const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const showAvailabilityCheck = searchParams.get('check') === 'true' && searchParams.get('vehicle') === 'sprinter';

  return (
    <div className="pt-20">
      {showAvailabilityCheck ? (
        <div className="min-h-screen bg-[#0A0A0A] py-8 px-4">
          <div className="max-w-2xl mx-auto bg-[#121212] border border-white/10 p-8">
            <AvailabilityForm />
          </div>
        </div>
      ) : (
        <BookingForm />
      )}
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  return (
    <>
      <ContactForm />
    </>
  );
};

// Layout with Navbar and Footer
const Layout = ({ children, showFooter = true }) => {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      {showFooter && <Footer />}
      <WhatsAppButton />
      <InstallPWA />
      <CookieConsent />
    </>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);

  if (showSplash) {
    return <SplashScreen onFinish={() => setShowSplash(false)} />;
  }

  return (
    <LanguageProvider>
      <AuthProvider>
        <div className="App bg-[#0A0A0A] min-h-screen">
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Layout><HomePage /></Layout>} />
              <Route path="/booking" element={<Layout><BookingPage /></Layout>} />
              <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<Layout showFooter={false}><AdminLogin /></Layout>} />
              <Route path="/admin" element={<Layout showFooter={false}><AdminDashboard /></Layout>} />
              
              {/* Legal Pages */}
              <Route path="/impressum" element={<Layout><Impressum /></Layout>} />
              <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />
              <Route path="/terms" element={<Layout><PrivacyPolicy /></Layout>} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
